import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import moment from "moment";
import { Typography, Button, Box, Tooltip } from "@mui/material";
import { MapPin, Eye, Check, Copy } from "lucide-react";
import SkillView from "bundles/Recruiter/components/SkillView";
import HTML from "bundles/common/components/HTML";
import JobOfferMediaGallery from "bundles/common/components/JobOfferMediaGallery";
import {
  getOfferStatus,
  annualyRemuneration,
  handleLinkedinShare,
  handleCopyClick,
} from "bundles/common/utils/utils";
import ConfirmModal from "bundles/common/components/ConfirmModal";
import sh from "bundles/common/utils/sh";
import { formatJobOfferLocation, getDuration } from "../../common/utils/utils";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { FacebookShareButton } from "react-share";

const INK = "hsl(178, 93%, 10%)";
const GREEN = "hsl(153, 68%, 25%)";
const MUTED = "hsl(178, 10%, 45%)";
const BORDER = "hsl(160, 10%, 90%)";
const SOFT_BG = "hsl(178, 30%, 98%)";

const STATUS_PILL = {
  draft: { bg: "hsla(38, 92%, 50%, 0.15)", color: "hsl(32, 81%, 33%)" },
  online: { bg: "hsla(153, 68%, 25%, 0.12)", color: GREEN },
  closed: { bg: "hsl(160, 10%, 92%)", color: MUTED },
};

const StatCard = ({ label, children }) => (
  <Box
    sx={{
      p: "12px 14px",
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      backgroundColor: SOFT_BG,
    }}
  >
    <Typography
      sx={{
        fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
        fontSize: "0.6875rem",
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: MUTED,
        mb: "4px",
      }}
    >
      {label}
    </Typography>
    <Typography
      component="div"
      sx={{
        fontFamily: "Segoe UI Bold, system-ui, sans-serif",
        fontSize: "0.95rem",
        color: INK,
      }}
    >
      {children}
    </Typography>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography
    gutterBottom
    sx={{
      fontFamily: "Segoe UI Bold, system-ui, sans-serif",
      fontSize: "1.0625rem",
      color: INK,
    }}
  >
    {children}
  </Typography>
);

const AdvantagePill = ({ children }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      px: "12px",
      py: "6px",
      borderRadius: "9999px",
      backgroundColor: "hsla(153, 68%, 25%, 0.1)",
      color: GREEN,
      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
      fontSize: "0.8125rem",
    }}
  >
    <Check size={14} strokeWidth={3} />
    {children}
  </Box>
);

const TextBlock = ({ titleId, text }) => (
  <Box>
    <SectionTitle>
      <FormattedMessage id={titleId} />
    </SectionTitle>
    <Box sx={{ color: "hsl(178, 12%, 28%)" }}>
      <HTML html={text} />
    </Box>
  </Box>
);

const JobOfferSummary = ({
  jobOffer,
  skills,
  isEdit = false,
  saveJobOffer = null,
  showNavigationMessage = () => {},
}) => {
  const authenticityToken = useSelector(state => state.authenticity_token);
  const i18nLocale = useSelector(state => state.i18nLocale);
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  const hard_skills = skills?.filter(skill => skill.category == "hard");
  const soft_skills = skills?.filter(skill => skill.category == "soft");
  const languages = skills?.filter(skill => skill.category == "lang");

  // Consistent button label typography (normal case, Segoe Semibold).
  const buttonSx = {
    textTransform: "none",
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
    fontSize: "1rem",
  };

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const statusMessage = getOfferStatus(jobOffer, 1000);
  const statusKey =
    jobOffer.offer_status === "online"
      ? "online"
      : jobOffer.offer_status === "closed"
        ? "closed"
        : "draft";
  const statusPill = STATUS_PILL[statusKey];

  const handleEditClicked = () => {
    if (isEdit && saveJobOffer) {
      saveJobOffer(false);
    } else {
      window.location.href = `/${i18nLocale}/recruiter/job_offer_creation?jobOffer=${jobOffer.id}`;
    }
  };

  // Repost a closed offer in place (re-publishes the same record, keeping its stats).
  const handleDuplicateClicked = () => {
    sh.post(`recruiter/renew_job_offers/${jobOffer.id}`, {
      authenticity_token: authenticityToken,
    })
      .then(() => {
        window.location.reload();
      })
      .catch((error) => {
        const needCredit = error?.response?.data?.error === "need_credit";
        enqueueSnackbar(
          intl.formatMessage({
            id: needCredit ? "credits_box.no_job_offer_credits" : "flash.unknown",
          }),
          { variant: needCredit ? "warning" : "error" }
        );
      });
  };

  const handleDeleteClicked = () => {
    setIsConfirmModalOpen(true);
  };

  const handleDeleteConfirmed = () => {
    showNavigationMessage(false);
    if (jobOffer.id) {
      let target =
        jobOffer.offer_status == "" || jobOffer.offer_status == "draft"
          ? "draft"
          : "closed";

      let action = target == "draft" ? "deleted" : "closed";
      sh.delete(`job_offer/${jobOffer.id}`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        data: {
          authenticity_token: authenticityToken,
        },
      })
        .then((response) => {
          const days_left = response.data.days_left;
          enqueueSnackbar(`Your ad has been succesfully ${action}. Your credit is still available for ${days_left} day(s).`, {
            variant: "success",
          });
          setTimeout(() => {
            window.location.href = `/${i18nLocale}/recruiter?tab=jobs&jobs_filter=${target}`;
          }, 1200);
        }).catch(() => {
          enqueueSnackbar(
            intl.formatMessage({
              id: "flash.unknown",
            }),
            { variant: "error" }
          );
        });
    } else {
      setTimeout(() => {
        window.location.href = `/${i18nLocale}/recruiter?tab=jobs&jobs_filter=online`;
        enqueueSnackbar("Your ad has been succesfully deleted", {
          variant: "success",
        });
      }, 100);
    }
  };

  const handlePublishClicked = () => {
    if (saveJobOffer) saveJobOffer(true);
  };

  const publicJobUrl = `${window.location.origin}/${i18nLocale}/jobs/${jobOffer?.encoded_job_title}/${jobOffer?.id}`;

  const advantageGroups = [
    { items: jobOffer?.offer_advantages?.advantages, prefix: "job_offer_edit.advantages" },
    { items: jobOffer?.offer_advantages?.schedules, prefix: "job_offer_edit.schedules" },
    { items: jobOffer?.offer_advantages?.environments, prefix: "job_offer_edit.environment" },
    { items: jobOffer?.offer_advantages?.primes, prefix: "job_offer_edit.primes" },
  ];
  const hasAdvantages = advantageGroups.some(group => group.items?.length > 0);

  const skillGroups = [
    { items: hard_skills, titleId: "candidate.profile.skills.hard", category: "hard", level: false },
    { items: soft_skills, titleId: "candidate.profile.skills.soft", category: "soft", level: false },
    { items: languages, titleId: "candidate.profile.skills.lang", category: "lang", level: undefined },
  ];

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: "880px",
          mx: "auto",
          textAlign: "left",
          backgroundColor: "#fff",
          border: `1px solid ${BORDER}`,
          borderRadius: "16px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
          overflow: "hidden",
        }}
      >
        {/* Brand accent strip */}
        <Box sx={{ height: "6px", backgroundColor: GREEN }} />

        <Box sx={{ p: { xs: "20px", md: "32px" } }}>
          {/* Title + status + share */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "16px",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                  fontSize: { xs: "1.375rem", md: "1.625rem" },
                  color: INK,
                  lineHeight: 1.2,
                }}
              >
                {jobOffer?.job_title}
              </Typography>
              {jobOffer?.locations?.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: "4px 12px",
                    mt: "8px",
                  }}
                >
                  {jobOffer.locations.map((location, index) => (
                    <Box
                      key={`${jobOffer.id}-location-${index}-${Object.values(location).join("-")}`}
                      sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
                    >
                      <MapPin size={15} color={MUTED} />
                      <Typography
                        sx={{
                          fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                          fontSize: "0.875rem",
                          color: MUTED,
                          textTransform: "capitalize",
                        }}
                      >
                        {formatJobOfferLocation(location)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "10px" }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: "12px",
                  py: "6px",
                  borderRadius: "9999px",
                  backgroundColor: statusPill.bg,
                  color: statusPill.color,
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  fontSize: "0.8125rem",
                  whiteSpace: "nowrap",
                }}
              >
                <FormattedMessage id={statusMessage.id} values={statusMessage.values} />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {jobOffer.offer_status !== "draft" && (
                  <>
                    <Box
                      onClick={() => handleLinkedinShare(publicJobUrl)}
                      sx={{ cursor: "pointer", display: "inline-flex" }}
                    >
                      <LinkedInIcon style={{ fill: "#0077B5" }} />
                    </Box>
                    <Box sx={{ display: "inline-flex", cursor: "pointer" }}>
                      <FacebookShareButton url={publicJobUrl}>
                        <FacebookIcon style={{ fill: "#1877F2" }} />
                      </FacebookShareButton>
                    </Box>
                    <Tooltip title={<FormattedMessage id="settings.copy_link" />}>
                      <Box
                        component="span"
                        sx={{ display: "inline-flex", cursor: "pointer", color: MUTED }}
                        onClick={() => handleCopyClick(publicJobUrl, intl, enqueueSnackbar)}
                      >
                        <Copy size={20} />
                      </Box>
                    </Tooltip>
                  </>
                )}

                {jobOffer.offer_status !== "draft" &&
                  jobOffer.view_count != null &&
                  jobOffer.view_count > 0 && (
                  <Tooltip
                    placement="left"
                    title={
                      <FormattedMessage
                        id="job_offer_summary.view_count_candidates"
                        values={{ people: jobOffer.view_count }}
                      />
                    }
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        color: MUTED,
                        cursor: "default",
                      }}
                    >
                      <Eye size={18} />
                      {jobOffer.view_count}
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>

          {/* Key metrics */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
              gap: "12px",
              mt: "24px",
            }}
          >
            <StatCard label={<FormattedMessage id="job_offer.company_industry" />}>
              {jobOffer?.company_industries?.length > 0
                ? jobOffer.company_industries
                  .map(industry =>
                    intl.formatMessage({ id: `company.company_industry.${industry}` })
                  )
                  .join(", ")
                : <FormattedMessage id="undefined" />}
            </StatCard>

            <StatCard
              label={
                <FormattedMessage
                  id={jobOffer?.end_date ? "job_offer.duration" : "job_offer.start_date"}
                />
              }
            >
              {jobOffer?.end_date ? (
                getDuration(jobOffer.start_date, jobOffer.end_date)
              ) : jobOffer?.start_date ? (
                moment(jobOffer.start_date).locale("fr").format("l")
              ) : (
                <FormattedMessage id="asap" />
              )}
            </StatCard>

            <StatCard label={<FormattedMessage id="job_offer.job_status" />}>
              {jobOffer?.job_statuses?.length > 0 ? (
                <Tooltip
                  title={jobOffer.job_statuses
                    .map(status =>
                      intl.formatMessage({ id: `candidate.status_map.${status}_hover` })
                    )
                    .join(", ")}
                  style={{ cursor: "pointer" }}
                  placement="bottom"
                >
                  <span>
                    {jobOffer.job_statuses
                      .map(status =>
                        intl.formatMessage({ id: `candidate.status_map.${status}` })
                      )
                      .join(", ")}
                  </span>
                </Tooltip>
              ) : (
                <FormattedMessage id="undefined" />
              )}
            </StatCard>

            <StatCard label={<FormattedMessage id="job_offer.monthly_offer" />}>
              {annualyRemuneration(jobOffer.monthly_offer)}
            </StatCard>
          </Box>

          {/* Divider */}
          <Box sx={{ height: "1px", backgroundColor: BORDER, my: "28px" }} />

          {/* Description sections */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {jobOffer?.description && (
              <TextBlock titleId="job_offer.description" text={jobOffer.description} />
            )}
            {jobOffer?.responsibilities && (
              <TextBlock
                titleId="job_offer_creation.responsibilities_label"
                text={jobOffer.responsibilities}
              />
            )}
            {jobOffer?.profile && (
              <TextBlock titleId="job_offer.profile" text={jobOffer.profile} />
            )}
            {jobOffer?.offer && (
              <TextBlock titleId="job_offer.offer" text={jobOffer.offer} />
            )}
            {jobOffer?.categories?.length > 0 && (
              <TextBlock
                titleId="public_job_offer_index.category"
                text={jobOffer.categories
                  .map(slug => intl.formatMessage({ id: `categories.${slug}` }))
                  .join(", ")}
              />
            )}
            <JobOfferMediaGallery jobOffer={jobOffer} sx={{ mt: "20px" }} />
          </Box>

          {/* Advantages */}
          {hasAdvantages && (
            <Box sx={{ mt: "28px" }}>
              <SectionTitle>
                <FormattedMessage id="job_offer_edit.advantages_title" />
              </SectionTitle>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mt: "4px" }}>
                {advantageGroups.flatMap(group =>
                  (group.items || []).map((value, index) => (
                    <AdvantagePill key={`${group.prefix}-${index}`}>
                      <FormattedMessage id={`${group.prefix}.${value}`} />
                    </AdvantagePill>
                  ))
                )}
              </Box>
            </Box>
          )}

          {/* Skills */}
          {skills?.length > 0 && (
            <Box sx={{ mt: "28px", display: "flex", flexDirection: "column", gap: "20px" }}>
              {skillGroups.map(
                group =>
                  group.items?.length > 0 && (
                    <Box key={group.category}>
                      <SectionTitle>
                        <FormattedMessage id={group.titleId} />
                      </SectionTitle>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                        {group.items.map((value, index) => (
                          <SkillView
                            key={`${group.category}-${index}`}
                            value={value}
                            category={group.category}
                            locale={i18nLocale}
                            level={group.level}
                          />
                        ))}
                      </Box>
                    </Box>
                  )
              )}
            </Box>
          )}
        </Box>

        {/* Actions */}
        <Box
          sx={{
            borderTop: `1px solid ${BORDER}`,
            backgroundColor: SOFT_BG,
            p: { xs: "20px", md: "24px 32px" },
          }}
        >
          {(jobOffer.offer_status == "" || jobOffer.offer_status == "draft") && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: "12px",
                  width: "100%",
                  maxWidth: "520px",
                }}
              >
                <Button
                  variant="rounded"
                  color="secondaryContained"
                  sx={{ ...buttonSx, flex: 1, width: "100%" }}
                  onClick={handlePublishClicked}
                >
                  <FormattedMessage id="job_offer.publish" />
                </Button>
                <Button
                  variant="rounded"
                  color="secondaryOutlined"
                  sx={{ ...buttonSx, flex: 1, width: "100%" }}
                  onClick={handleEditClicked}
                >
                  {isEdit ? (
                    <FormattedMessage id="job_offer.save" />
                  ) : (
                    <FormattedMessage id="job_offer.edit" />
                  )}
                </Button>
              </Box>
              <Button variant="text" sx={buttonSx} onClick={handleDeleteClicked}>
                <FormattedMessage id="job_offer.delete_draft" />
              </Button>
            </Box>
          )}

          {jobOffer.offer_status == "online" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: "12px",
                width: "100%",
                maxWidth: "520px",
                mx: "auto",
              }}
            >
              <Button
                variant="rounded"
                color="secondaryContained"
                sx={{ ...buttonSx, flex: 1, width: "100%" }}
                onClick={handleEditClicked}
              >
                {isEdit ? (
                  <FormattedMessage id="job_offer.save" />
                ) : (
                  <FormattedMessage id="job_offer.edit" />
                )}
              </Button>
              <Button
                variant="rounded"
                color="secondaryOutlined"
                sx={{ ...buttonSx, flex: 1, width: "100%" }}
                onClick={handleDeleteClicked}
              >
                <FormattedMessage id="job_offer.soft_delete" />
              </Button>
            </Box>
          )}

          {jobOffer.offer_status == "closed" && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="rounded"
                color="secondaryContained"
                sx={{ ...buttonSx, width: "100%", maxWidth: "320px" }}
                onClick={handleDuplicateClicked}
              >
                <FormattedMessage id="job_offer.repost" />
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        setIsOpen={setIsConfirmModalOpen}
        handleConfirm={handleDeleteConfirmed}
        labelId={
          jobOffer.offer_status == "online"
            ? "close_offer_confirmation"
            : "delete_offer_confirmation"
        }
      />
    </>
  );
};

export default JobOfferSummary;
